import { Checkbox, Empty, Input, Switch } from 'antd';
import { cloneDeep, filter, includes } from 'lodash';
import React, { useMemo, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { IFilterByEvents } from '@src/emp-3.0/components/Sankey/Configurations/FilterByEvents/typed';
import { useSankeyStore } from '@src/emp-3.0/components/Sankey/store';
import { humanize } from '@utils';

import { CheckboxStyled } from './styled';

const FilterByEvents: React.FC<IFilterByEvents> = ({
  data = [],
  onChange,
  onSwitchClick,
  checkedOnly,
}) => {
  const [searchText, setSearchText] = useState<string>('');

  const { sankeyRequestBody } = useSankeyStore((state) => state);
  const { landing_events, network_request_enabled } = sankeyRequestBody;

  const filteredData = useMemo(() => {
    return filter(data, (item) => {
      const isMatchSearch = includes(item.name?.toLowerCase(), searchText.toLowerCase());
      if (checkedOnly) {
        return item.checked && isMatchSearch;
      }
      return isMatchSearch;
    });
  }, [data, searchText, checkedOnly]);

  const handleChange = (checked, index) => {
    const cloneData = cloneDeep(data);
    const name = filteredData[index].name;
    const newData =
      cloneData?.map((item) => {
        if (item.name === name) {
          return {
            ...item,
            checked,
          };
        }
        return item;
      }) || [];
    const selectedEventsName = newData.filter((item) => item.checked).map((item) => item.name);
    onChange?.(newData, selectedEventsName);
  };

  const handleSelectAll = (e) => {
    const cloneData = cloneDeep(data);
    const checked = e.target.checked;
    const candidateList = cloneData.filter((i) => !i.disabled);
    if (!checked) {
      candidateList?.forEach((i) => {
        i.checked = false;
      });
      onChange?.(candidateList, []);
    } else {
      candidateList?.forEach((i) => {
        i.checked = true;
      });
      const nameList = candidateList.map((i) => i.name);
      onChange?.(candidateList, [...nameList]);
    }
  };

  const [isAllSelected, isSomeSelected] = useMemo(() => {
    if (data?.length) {
      const candidateList = data.filter((i) => !i.disabled);
      const allChecked = candidateList.every((i) => i.checked);
      const someChecked = candidateList.some((i) => i.checked);
      return [candidateList.length && allChecked, candidateList.length && someChecked];
    }
    return [false, false];
  }, [data]);

  return (
    <div style={{ paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Input
        addonBefore={<SearchOutlined />}
        value={searchText || undefined}
        placeholder="search..."
        onChange={(e) => setSearchText(e.target.value)}
      />
      <Checkbox
        style={{
          padding: '12px 8px',
          marginLeft: 0,
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        }}
        onChange={handleSelectAll}
        checked={isAllSelected}
        indeterminate={isSomeSelected && !isAllSelected}
      >
        Select All
      </Checkbox>
      <div style={{ height: 245, overflowY: 'scroll', display: 'flex', flexDirection: 'column' }}>
        {filteredData.length > 0 ? (
          filteredData.map((item, index) => (
            <CheckboxStyled
              key={`${item.name}_${index}`}
              style={{
                padding: '12px 8px',
                marginLeft: 0,
                borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
              }}
              checked={item.checked}
              disabled={item.disabled}
              onChange={(e) => handleChange(e.target.checked, index)}
            >
              <span
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <span>{item.name}</span>
                {item.count !== 0 && (
                  <span style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '12px' }}>
                    {humanize(item.count, 2)}
                  </span>
                )}
              </span>
            </CheckboxStyled>
          ))
        ) : (
          <Empty
            description="No data"
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          />
        )}
      </div>
      <div style={{ padding: '4px 8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Switch
          checked={network_request_enabled === 1}
          disabled={landing_events?.[0].event_name === 'conviva_network_request'}
          size="small"
          onClick={onSwitchClick}
        />
        <span>Network request enabled</span>
      </div>
    </div>
  );
};

export default FilterByEvents;
